import { useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { EmptyState, Pill, FilterRowLabel, HostChip, showToast } from "../primitives";
import {
  Panel,
  PanelRow,
  PANEL_COL_SURFACE,
  PANEL_COL_VERIFICATION,
  PANEL_COL_STATUS,
  PANEL_COL_TIME,
} from "./Panel";
import { Markdown } from "../Markdown";
import { NewPageModal } from "../page/NewPageModal";
import { useChildNodes } from "../../hooks/use-child-nodes";
import { useWorkspacePages } from "../../../lib/hooks/use-kuvio";
import { useIsOwner } from "../../hooks/use-is-owner";
import {
  useArchiveNodeAction,
  useRestoreNodeAction,
  useDeleteNodeAction,
} from "../../hooks/use-node-actions";
import { PanelRowMenu } from "./PanelRowMenu";
import { ConfirmDeleteRowModal } from "./ConfirmDeleteRowModal";
import { ArchivedBadge, ShowArchivedChip, useShowArchived } from "./panel-archive";
import { pageSurfaceMeta, pageStatusMeta, PAGE_SURFACES, PAGE_STATUSES } from "../../lib/page-meta";
import type { LibraryNode } from "../../../lib/types";
import { VerificationBadge } from "../verification/VerificationBadge";

interface PagesPanelProps {
  /** Parent node — pages owned by this agent, or (workspaceWide) the root. */
  nodeId: string;
  /** Workspace cockpit variant: list every page in the workspace, not just direct children. */
  workspaceWide?: boolean;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Sprint 18 — true when a page's frontmatter links it to any priority. */
function pageHasPriorityLink(frontmatter: Record<string, unknown> | null | undefined): boolean {
  const ents = frontmatter?.entities;
  return (
    Array.isArray(ents) &&
    ents.some((e) => typeof e === "object" && e !== null && (e as Record<string, unknown>).kind === "priority")
  );
}

/** Sprint 18 — "Hide linked pages" toggle, persisted per scope in localStorage. */
function useHideLinkedPages(scopeId: string): [boolean, (v: boolean) => void] {
  const key = `kuvio_pages_hide_linked_${scopeId}`;
  const [value, setValue] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  const set = (v: boolean) => {
    setValue(v);
    try {
      localStorage.setItem(key, v ? "1" : "0");
    } catch {
      /* storage disabled — keep in-memory only */
    }
  };
  return [value, set];
}

// Filter selection persists per-panel in sessionStorage (resets on new session).
function filterKey(nodeId: string, dim: "surface" | "status"): string {
  return `pages.filter.${nodeId}.${dim}`;
}
function loadFilter(nodeId: string, dim: "surface" | "status"): string {
  try { return sessionStorage.getItem(filterKey(nodeId, dim)) ?? "all"; } catch { return "all"; }
}
function saveFilter(nodeId: string, dim: "surface" | "status", value: string): void {
  try {
    if (value === "all") sessionStorage.removeItem(filterKey(nodeId, dim));
    else sessionStorage.setItem(filterKey(nodeId, dim), value);
  } catch { /* ignore */ }
}

export function PagesPanel({ nodeId, workspaceWide = false }: PagesPanelProps) {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useShowArchived("pages", nodeId);
  const childPages = useChildNodes(workspaceWide ? undefined : nodeId, "page", showArchived);
  const workspacePages = useWorkspacePages(workspaceWide);
  const query = workspaceWide ? workspacePages : childPages;

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [surfaceFilter, setSurfaceFilter] = useState(() => loadFilter(nodeId, "surface"));
  const [statusFilter, setStatusFilter] = useState(() => loadFilter(nodeId, "status"));
  const [hideLinked, setHideLinked] = useHideLinkedPages(nodeId);

  const all = query.data ?? [];
  const items = useMemo(
    () =>
      all.filter(
        (p) =>
          (surfaceFilter === "all" || p.surface === surfaceFilter) &&
          (statusFilter === "all" || p.status === statusFilter) &&
          (!hideLinked || !pageHasPriorityLink(p.frontmatter)),
      ),
    [all, surfaceFilter, statusFilter, hideLinked],
  );

  function setSurface(v: string) { setSurfaceFilter(v); saveFilter(nodeId, "surface", v); }
  function setStatus(v: string) { setStatusFilter(v); saveFilter(nodeId, "status", v); }

  const filtersActive = surfaceFilter !== "all" || statusFilter !== "all";

  return (
    <>
      <Panel
        title="Pages"
        count={all.length}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        isEmpty={all.length === 0}
        empty={
          showArchived ? (
            <EmptyState>No archived items. Items you archive will appear here for restore.</EmptyState>
          ) : (
            <EmptyState>
              No pages yet.{" "}
              <button onClick={() => setNewOpen(true)} style={emptyLinkStyle}>+ New page</button>
            </EmptyState>
          )
        }
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <HideLinkedChip value={hideLinked} onChange={setHideLinked} />
            <ShowArchivedChip value={showArchived} onChange={setShowArchived} />
            <button onClick={() => setNewOpen(true)} style={newButtonStyle} title="New page">
              <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" />
              New page
            </button>
          </div>
        }
      >
        {all.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <ChipRow
              label="Surface:"
              options={["all", ...PAGE_SURFACES]}
              selected={surfaceFilter}
              labelFor={(v) => (v === "all" ? "All" : pageSurfaceMeta(v).label)}
              onSelect={setSurface}
            />
            <ChipRow
              label="Status:"
              options={["all", ...PAGE_STATUSES]}
              selected={statusFilter}
              labelFor={(v) => (v === "all" ? "All" : pageStatusMeta(v).label)}
              onSelect={setStatus}
            />
          </div>
        )}
        {all.length > 0 && items.length === 0 ? (
          <div style={noMatchStyle}>
            No pages match these filters.
            {filtersActive && (
              <button onClick={() => { setSurface("all"); setStatus("all"); }} style={emptyLinkStyle}>Clear</button>
            )}
          </div>
        ) : (
          items.map((page) => (
            <PageRow
              key={page.id}
              page={page}
              expanded={expandedId === page.id}
              onToggle={() => setExpandedId((cur) => (cur === page.id ? null : page.id))}
              onOpen={() => navigate(`/library/${page.id}`)}
            />
          ))
        )}
      </Panel>
      <NewPageModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        parentId={nodeId}
        onCreated={(node) => navigate(`/library/${node.id}`)}
      />
    </>
  );
}

function ChipRow({
  label,
  options,
  selected,
  labelFor,
  onSelect,
}: {
  label?: string;
  options: string[];
  selected: string;
  labelFor: (v: string) => string;
  onSelect: (v: string) => void;
}) {
  return (
    <div style={chipRowStyle}>
      {label ? <FilterRowLabel>{label}</FilterRowLabel> : null}
      {options.map((opt) => {
        const active = selected === opt;
        return (
          <button
            key={opt}
            onClick={() => onSelect(opt)}
            aria-pressed={active}
            style={{
              ...chipStyle,
              background: active ? "var(--color-text-primary)" : "var(--color-background-secondary)",
              color: active ? "var(--color-background-primary)" : "var(--color-text-secondary)",
            }}
          >
            {labelFor(opt)}
          </button>
        );
      })}
    </div>
  );
}

function PageRow({
  page,
  expanded,
  onToggle,
  onOpen,
}: {
  page: LibraryNode;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const surface = pageSurfaceMeta(page.surface);
  const status = pageStatusMeta(page.status);
  const body = page.document ?? "";

  const archiveNode = useArchiveNodeAction();
  const restoreNode = useRestoreNodeAction();
  const deleteNode = useDeleteNodeAction();
  const canDelete = useIsOwner();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isArchived = !!page.archived_at;

  function handleArchive() {
    archiveNode.mutate(
      { id: page.id },
      {
        onSuccess: () =>
          showToast(`Archived '${page.name}'`, "success", false, {
            label: "Undo",
            onClick: () => restoreNode.mutate({ id: page.id }),
          }, 10000),
        onError: (e) => showToast(e instanceof Error ? e.message : "Archive failed", "error", true),
      },
    );
  }

  function handleRestore() {
    restoreNode.mutate(
      { id: page.id },
      {
        onSuccess: () => showToast(`Restored '${page.name}'`),
        onError: (e) => showToast(e instanceof Error ? e.message : "Restore failed", "error", true),
      },
    );
  }

  return (
    <div>
      <PanelRow
        onClick={onToggle}
        title={expanded ? "Collapse" : "Show page"}
        columns={[
          { key: "surface", width: PANEL_COL_SURFACE, content: <span style={{ opacity: isArchived ? 0.6 : 1 }}><Pill variant={surface.variant}>{surface.label}</Pill></span> },
          { key: "verify", width: PANEL_COL_VERIFICATION, content: <VerificationBadge frontmatter={page.frontmatter} /> },
          {
            key: "status",
            width: PANEL_COL_STATUS,
            content: (
              <span style={{ ...statusBadgeStyle, opacity: isArchived ? 0.6 : 1 }} title={`Status: ${status.label}`}>
                <span style={{ ...statusDotStyle, background: status.dotColor }} aria-hidden="true" />
                {status.label}
              </span>
            ),
          },
          {
            key: "name",
            flex: true,
            content: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, opacity: isArchived ? 0.6 : 1 }}>
                <span style={nameStyle}>{page.name}</span>
                {isArchived && <ArchivedBadge />}
              </span>
            ),
          },
          ...(page.created_via ? [{ key: "via", width: 60, content: <HostChip kind={page.created_via} /> }] : []),
          {
            key: "time",
            width: PANEL_COL_TIME,
            align: "right" as const,
            content: <span style={timeStyle}>{formatRelativeTime(page.updated_at)}</span>,
          },
        ]}
        trailing={
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              aria-label={`Open ${page.name}`}
              style={openIconStyle}
            >
              <i className="ti ti-external-link" style={{ fontSize: 14 }} aria-hidden="true" />
            </button>
            <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
              <PanelRowMenu
                isArchived={isArchived}
                canDelete={canDelete}
                onArchive={handleArchive}
                onRestore={handleRestore}
                onDelete={() => setConfirmDelete(true)}
              />
            </span>
          </div>
        }
      />
      <ConfirmDeleteRowModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        entityKind="page"
        entityName={page.name}
        entityId={page.id}
        backRefKind="node"
        onConfirm={async ({ force }) => {
          await deleteNode.mutateAsync({ id: page.id, force });
          showToast(`Deleted '${page.name}'`);
        }}
      />
      {expanded && (
        <div style={bodyWrapStyle}>
          {body.trim() ? (
            <Markdown>{body}</Markdown>
          ) : (
            <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>This page is empty.</div>
          )}
          <div style={{ marginTop: 10 }}>
            <button onClick={onOpen} style={openFullStyle}>
              Open full page
              <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Sprint 18 — Q10 dedupe toggle. Off by default; matches the archive chip look. */
function HideLinkedChip({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      title={value ? "Show pages linked to priorities" : "Hide pages already linked to a priority"}
      aria-pressed={value}
      style={{ ...hideLinkedChipStyle, ...(value ? hideLinkedChipActiveStyle : {}) }}
    >
      <i className={`ti ti-link${value ? "-off" : ""}`} style={{ fontSize: 13 }} aria-hidden="true" />
      {value ? "Show linked" : "Hide linked"}
    </button>
  );
}

const hideLinkedChipStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const hideLinkedChipActiveStyle: CSSProperties = {
  background: "var(--color-background-secondary, rgba(0,0,0,0.04))",
  color: "var(--color-text-primary)",
  borderColor: "var(--color-border-primary, var(--color-border-secondary))",
};

const chipRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 4,
  marginBottom: 4,
};

const chipStyle: CSSProperties = {
  border: "none",
  borderRadius: "var(--border-radius-sm)",
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const newButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
  padding: 0,
};

const emptyLinkStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  padding: 0,
  fontFamily: "var(--font-sans)",
};

const noMatchStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  padding: "8px",
  display: "flex",
  gap: 8,
  alignItems: "center",
};

const bodyWrapStyle: CSSProperties = {
  margin: "2px 8px 8px",
  padding: "10px 12px",
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  maxHeight: 600,
  overflowY: "auto",
};

const statusBadgeStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  whiteSpace: "nowrap",
};

const statusDotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
};

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const timeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
  marginLeft: "auto",
};

const openIconStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 2,
  display: "flex",
  alignItems: "center",
};

const openFullStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "var(--font-sans)",
};
